import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../../../data/BaseUrl";
import { fetchLeaveData, buildMultiDateLeaveMap, COMPLIANCE } from "../daily-tracker/leaveUtils";

const FILTERS = {
  NON_COMPLIANT:    "non_compliant",
  FREQUENT_ABSENT:  "frequent_absent",
  WORST_OFFENDERS:  "worst_offenders",
};

const FREQUENT_THRESHOLD = 3;

const DisciplineComplianceSection = ({ allShifts = [], dateFrom, dateTo }) => {
  const token = useSelector((state) => state.auth?.user?.token);

  const [leaveRecords, setLeaveRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Fetch leave data for the date range
  useEffect(() => {
    if (!token || !dateFrom || !dateTo) return;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const records = await fetchLeaveData(BASE_URL, token, dateFrom, dateTo, controller.signal);
        setLeaveRecords(records);
      } catch (err) {
        if (err.name === "AbortError") return;
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [token, dateFrom, dateTo]);

  // Build per-staff compliance stats
  const staffStats = useMemo(() => {
    if (allShifts.length === 0) return [];

    const leaveMap = buildMultiDateLeaveMap(leaveRecords);

    // Group shifts by userId
    const byUser = {};
    allShifts.forEach((shift) => {
      const uid = shift.userId;
      if (!byUser[uid]) {
        byUser[uid] = {
          userId: uid,
          name: shift.name,
          gymName: shift.gymName,
          accountType: shift.accountType,
          dayMap: {}, // date -> { expected, present }
        };
      }
      const u = byUser[uid];
      // Update name/gym to latest seen
      if (shift.name && shift.name !== "—") u.name = shift.name;
      if (shift.gymName && shift.gymName !== "—") u.gymName = shift.gymName;

      if (!u.dayMap[shift.date]) {
        u.dayMap[shift.date] = { expected: false, present: false };
      }
      const day = u.dayMap[shift.date];
      // A shift record existing means this staff was expected on this date
      day.expected = true;
      // Check if they showed up
      const cs = shift.att?.checkin_status;
      const co = shift.att?.checkout_status;
      if (cs === "Checked-In" || co === "checked-out") {
        day.present = true;
      }
    });

    // Compute stats per user
    const stats = Object.values(byUser).map((u) => {
      let expected = 0;
      let present = 0;
      let onLeave = 0;
      let pending = 0;
      let rejected = 0;
      let noLeave = 0;

      Object.entries(u.dayMap).forEach(([date, day]) => {
        if (!day.expected) return;
        expected++;
        if (day.present) {
          present++;
          return;
        }
        // Absent day — check leave map
        const key = `${u.userId}_${date}`;
        const leave = leaveMap[key];
        if (leave) {
          const status = leave._raw;
          if (status === "1") onLeave++;
          else if (status === "0") pending++;
          else if (status === "2") rejected++;
          else noLeave++;
        } else {
          noLeave++;
        }
      });

      const absences = expected - present;
      const compliancePct = expected > 0
        ? Math.round(((present + onLeave) / expected) * 100)
        : 100;

      return {
        userId: u.userId,
        name: u.name,
        gymName: u.gymName,
        accountType: u.accountType,
        expected,
        present,
        absences,
        onLeave,
        pending,
        rejected,
        noLeave,
        compliancePct,
      };
    });

    return stats
      .filter((s) => s.expected > 0)
      .sort((a, b) => (b.noLeave + b.rejected) - (a.noLeave + a.rejected));
  }, [allShifts, leaveRecords]);

  // Filter groups
  const nonCompliant = useMemo(
    () => staffStats.filter((s) => s.noLeave + s.rejected > 0),
    [staffStats]
  );
  const frequentAbsentees = useMemo(
    () => staffStats.filter((s) => s.absences >= FREQUENT_THRESHOLD),
    [staffStats]
  );
  const worstOffenders = useMemo(
    () => staffStats.filter((s) => (s.noLeave + s.rejected > 0) && s.absences >= FREQUENT_THRESHOLD),
    [staffStats]
  );

  const displayList = useMemo(() => {
    if (activeFilter === FILTERS.NON_COMPLIANT) return nonCompliant;
    if (activeFilter === FILTERS.FREQUENT_ABSENT) return frequentAbsentees;
    if (activeFilter === FILTERS.WORST_OFFENDERS) return worstOffenders;
    return staffStats;
  }, [activeFilter, staffStats, nonCompliant, frequentAbsentees, worstOffenders]);

  const toggleFilter = (key) => {
    setActiveFilter((prev) => (prev === key ? null : key));
  };

  const pctClass = (pct) => {
    if (pct >= 80) return "disc-pct--good";
    if (pct >= 50) return "disc-pct--warn";
    return "disc-pct--bad";
  };

  if (loading) {
    return (
      <div className="disc-section">
        <h4>Leave Compliance</h4>
        <div className="disc-loading">
          <span className="disc-spinner" />
          Loading leave data...
        </div>
      </div>
    );
  }

  return (
    <div className="disc-section">
      <h4>Leave Compliance</h4>

      <div className="disc-flags">
        <button
          type="button"
          className={`disc-flag${activeFilter === FILTERS.NON_COMPLIANT ? " disc-flag--active" : ""}`}
          onClick={() => toggleFilter(FILTERS.NON_COMPLIANT)}
        >
          <span className="disc-flag__label">Non-Compliant</span>
          <span className="disc-flag__value">{nonCompliant.length}</span>
        </button>
        <button
          type="button"
          className={`disc-flag${activeFilter === FILTERS.FREQUENT_ABSENT ? " disc-flag--active" : ""}`}
          onClick={() => toggleFilter(FILTERS.FREQUENT_ABSENT)}
        >
          <span className="disc-flag__label">Frequent Absentees</span>
          <span className="disc-flag__value">{frequentAbsentees.length}</span>
        </button>
        <button
          type="button"
          className={`disc-flag${activeFilter === FILTERS.WORST_OFFENDERS ? " disc-flag--active" : ""}`}
          onClick={() => toggleFilter(FILTERS.WORST_OFFENDERS)}
        >
          <span className="disc-flag__label">Worst Offenders</span>
          <span className="disc-flag__value">{worstOffenders.length}</span>
        </button>
      </div>

      {displayList.length === 0 ? (
        <div className="disc-empty">No compliance violations found.</div>
      ) : (
        <table className="disc-table">
          <thead>
            <tr>
              <th>Staff Name</th>
              <th>Gym</th>
              <th>Role</th>
              <th>Days Expected</th>
              <th>Absences</th>
              <th>No Leave</th>
              <th>Rejected</th>
              <th>Pending</th>
              <th>On Leave</th>
              <th>Compliance %</th>
            </tr>
          </thead>
          <tbody>
            {displayList.map((s) => (
              <tr key={s.userId}>
                <td style={{ fontWeight: 600 }}>{s.name}</td>
                <td style={{ fontSize: 12, color: "#666" }}>{s.gymName}</td>
                <td style={{ fontSize: 12 }}>
                  {s.accountType === "trainer" ? "Trainer" : "Sales Exec"}
                </td>
                <td>{s.expected}</td>
                <td>
                  <span style={{ color: s.absences >= FREQUENT_THRESHOLD ? "#dc3545" : "#555", fontWeight: 600 }}>
                    {s.absences}
                  </span>
                </td>
                <td>
                  <span style={{ color: s.noLeave > 0 ? "#dc3545" : "#555", fontWeight: 600 }}>
                    {s.noLeave}
                  </span>
                </td>
                <td>
                  <span style={{ color: s.rejected > 0 ? "#dc3545" : "#555", fontWeight: 600 }}>
                    {s.rejected}
                  </span>
                </td>
                <td>
                  <span style={{ color: s.pending > 0 ? "#fd7e14" : "#555" }}>
                    {s.pending}
                  </span>
                </td>
                <td>{s.onLeave}</td>
                <td>
                  <span className={pctClass(s.compliancePct)} style={{ fontWeight: 600 }}>
                    {s.compliancePct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DisciplineComplianceSection;
